import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { useStrategySimulation } from '../../hooks/useStrategySimulation';
import { TemplatesPanel } from './TemplatesPanel';
import { EconomyTab } from './EconomyTab';
import { MilitaryTab } from './MilitaryTab';
import { ScienceTab } from './ScienceTab';
import { OutcomesPanel } from './OutcomesPanel';
import type { StrategyShellProps } from '../../pages/Strategy';
import type { StrategyTabKey } from '../../types/strategy';

// `/strategy` is a REALM_GROUP sibling route (constants/realmNav.ts), so `Layout.tsx` always
// renders `SiblingRow` sticky at `top: 0`/`z-20` above this shell on mobile. Matches
// MobileWarRoomShell's/MobileSciencesShell's existing SIBLING_ROW_HEIGHT_PX precedent —
// SiblingRow's own rendered height: 44px `minHeight` (its Link children) + 1px `borderBottom`.
const SIBLING_ROW_HEIGHT_PX = 45;

const TABS: readonly { key: StrategyTabKey; label: string }[] = [
  { key: 'economy', label: 'Buildings' },
  { key: 'military', label: 'Military' },
  { key: 'science', label: 'Sciences' },
  { key: 'outcome', label: 'Outcome' },
];

/**
 * SF-172 Component 3: mobile-native tab IA for the Strategy Planner (Buildings/Military/
 * Sciences), reusing EconomyTab/MilitaryTab/ScienceTab with `inputSize="touch"`. SF-173 adds a 4th
 * Outcome tab reusing `OutcomesPanel` at `density="comfortable"`.
 *
 * Sticky sub-header (Templates trigger + tab bar, `top: SIBLING_ROW_HEIGHT_PX`) fixes two Phase 2
 * findings at once: the tab strip no longer scrolls out of reach while editing EconomyTab's
 * ~940px of content, and Save (reachable only via the Templates trigger's dropdown) is one tap
 * away at any scroll position.
 */
export function MobileStrategyShell({
  province,
  formState,
  dispatch,
  mobileTab,
  setMobileTab,
  totalLand,
  population,
  race,
  existingBooks,
  templates,
  activeStrategyRef,
  templatesLoading,
  currentTemplateId,
  isDirty,
  bulkMutationVersion,
  handleLoadTemplate,
  handleActivateCustom,
  handleActivatePremade,
  handleDeactivate,
  handleSaveNew,
  handleSaveChanges,
  handleRename,
  handleDelete,
}: StrategyShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const projected = useStrategySimulation(formState, province);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold outline-none" style={{ color: COLORS.text.primary }}>
        Strategy Planner
      </h1>

      {/* SF-190 Component 9: always-visible explainer, additive below the existing visible <h1> —
          no change to the <h1> itself or its focus-management pattern (Phase 3 Decision 2). */}
      <p className="text-xs" style={{ color: COLORS.text.tertiary }}>
        Set target percentages for how your land should be allocated. This doesn't build anything by
        itself — visit Buildings and use Fill Strategy, or type quantities yourself.
      </p>

      <div
        style={{
          position: 'sticky',
          top: SIBLING_ROW_HEIGHT_PX,
          zIndex: 10,
          backgroundColor: COLORS.bg,
        }}
        className="space-y-2 pb-2"
      >
        {/* Templates trigger — own full-width row */}
        <TemplatesPanel
          templates={templates}
          activeStrategyRef={activeStrategyRef}
          onLoadTemplate={handleLoadTemplate}
          onActivateCustom={handleActivateCustom}
          onActivatePremade={handleActivatePremade}
          onDeactivate={handleDeactivate}
          onSaveNew={handleSaveNew}
          onSaveChanges={handleSaveChanges}
          onRename={handleRename}
          onDelete={handleDelete}
          isDirty={isDirty}
          currentTemplateId={currentTemplateId}
          loading={templatesLoading}
        />

        {/* Tab bar */}
        <div
          className="flex border-b overflow-x-auto"
          style={{ borderColor: COLORS.border }}
          role="tablist"
          aria-label="Strategy Planner sections"
        >
          {TABS.map((tab) => {
            const isActive = mobileTab === tab.key;
            return (
              <button
                key={tab.key}
                id={`strategy-tab-${tab.key}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`strategy-panel-${tab.key}`}
                onClick={() => {
                  setMobileTab(tab.key);
                  // SF-173: in-page tab switch, not a route change — Layout.tsx scrolls the
                  // document (CLAUDE.md), not <main>, mirroring Layout.tsx's own SF-132
                  // route-change scrollTo.
                  window.scrollTo(0, 0);
                }}
                className="flex-1 px-2 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap text-center"
                style={{
                  minHeight: 44,
                  borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                  color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {mobileTab === 'economy' && (
        <div role="tabpanel" id="strategy-panel-economy" aria-labelledby="strategy-tab-economy">
          <EconomyTab
            state={formState}
            dispatch={dispatch}
            totalLand={totalLand}
            inputSize="touch"
            resetKey={bulkMutationVersion}
          />
        </div>
      )}
      {mobileTab === 'military' && (
        <div role="tabpanel" id="strategy-panel-military" aria-labelledby="strategy-tab-military">
          <MilitaryTab
            state={formState}
            dispatch={dispatch}
            totalLand={totalLand}
            population={population}
            race={race}
            inputSize="touch"
            resetKey={bulkMutationVersion}
          />
        </div>
      )}
      {mobileTab === 'science' && (
        <div role="tabpanel" id="strategy-panel-science" aria-labelledby="strategy-tab-science">
          <ScienceTab
            state={formState}
            dispatch={dispatch}
            totalLand={totalLand}
            existingBooks={existingBooks}
            inputSize="touch"
            resetKey={bulkMutationVersion}
          />
        </div>
      )}
      {mobileTab === 'outcome' && (
        <div role="tabpanel" id="strategy-panel-outcome" aria-labelledby="strategy-tab-outcome" tabIndex={0}>
          <OutcomesPanel projected={projected} density="comfortable" />
        </div>
      )}
    </div>
  );
}
